import {Formik, FormikConfig, FormikHelpers, FormikProps, FormikValues} from 'formik'
import React, {FC, MutableRefObject, useEffect, useRef, useState} from 'react'
import {Accordion} from 'react-bootstrap'
import {ReportEvidence} from '../modules/judol/judol-list/core/_models'
import * as Yup from 'yup'
import ItemUnitsSelect from '../modules/itemUnits/components/ItemUnitsSelect'
import CurrencyInput from 'react-currency-input-field'
import clsx from 'clsx'
import EvidenceSelect from '../modules/evidence/components/EvidenceSelect'
import {formatNumber} from '../utils/string'

type Props = {
  isLoading: boolean
  isEditEvidence: boolean
  evidences: ReportEvidence[]
  setEvidence: React.Dispatch<React.SetStateAction<ReportEvidence[]>>
  setEditEvidence: React.Dispatch<React.SetStateAction<boolean>>
  disabled?: boolean
  useAccordion?: boolean
  resetValidation?: boolean
}

const schema = Yup.object().shape({
  evidenceId: Yup.number().required('Nama Barang wajib diisi'),
  qty: Yup.number().required('Jumlah wajib diisi').min(0, 'Jumlah harus bilangan positif'),
  valueAmt: Yup.number().min(0, 'Jumlah harus bilangan positif'),
})

const ReportEvidenceForm: FC<Props> = ({
  isLoading,
  isEditEvidence,
  evidences,
  // itemUnit,
  setEditEvidence,
  setEvidence,
  // setItemUnit,
  disabled = false,
  useAccordion = true,
  resetValidation = false,
}) => {
  const ref = useRef<FormikProps<ReportEvidence> | null>(null)
  const [initialOptionEvidence, setInitialOptionEvidence] = useState<any[]>([])
  const [disableValueAmt, setDisableValueAmt] = useState(false)

  useEffect(() => {
    ref.current?.setTouched({})
  }, [resetValidation])

  const FormComp = () => {
    return (
      <Formik
        onSubmit={(values: FormikValues, helper: FormikHelpers<ReportEvidence>) => {
          if (isEditEvidence) {
            const idx = values.index
            helper.resetForm({
              values: {
                description: '',
                evidenceId: undefined,
                evidenceName: '',
                gender: 0,
                id: undefined,
                itemUnitId: undefined,
                itemUnitName: '',
                name: '',
                key: '',
                qty: undefined,
                valueAmt: undefined,
                index: undefined,
              },
            })

            if (idx >= 0) {
              setEvidence(evidences.map((item, i) => (i === idx ? values : item)))
              setEditEvidence(false)
            }
            return
          }

          helper.resetForm({
            values: {
              description: '',
              evidenceId: undefined,
              evidenceName: '',
              gender: 0,
              id: undefined,
              itemUnitId: undefined,
              itemUnitName: '',
              name: '',
              key: '',
              qty: undefined,
              valueAmt: undefined,
              index: undefined,
            },
          })

          setEvidence([...evidences, values])
          helper.setSubmitting(false)
        }}
        initialValues={
          {
            description: '',
            evidenceId: undefined,
            evidenceName: '',
            gender: 0,
            id: undefined,
            itemUnitId: undefined,
            itemUnitName: '',
            name: '',
            key: '',
            qty: undefined,
            valueAmt: undefined,
          } as ReportEvidence
        }
        innerRef={ref}
        validationSchema={schema}
      >
        {(formik) => (
          <div className='container-fluid'>
            <label className='form-section-title'>
              <i className='fa-regular fa-file-lines'></i> Form Barang Bukti
            </label>
            <div className='row mb-2'>
              <div className='col-md-6 mb-3'>
                <label htmlFor='report_evidence' className='form-label required'>
                  Nama Barang
                </label>
                <EvidenceSelect
                  form={formik}
                  field={formik.getFieldProps('evidenceId')}
                  className={clsx(
                    {
                      'is-invalid': formik.touched.evidenceId && formik.errors.evidenceId,
                    },
                    {
                      'is-valid': formik.touched.evidenceId && !formik.errors.evidenceId,
                    }
                  )}
                  disabled={disabled}
                  onChange={(e) => {
                    formik.setValues({
                      evidenceId: e.value,
                      evidenceName: e.label,
                      itemUnitId: e.itemUnit.id,
                      itemUnitName: e.itemUnit.name,
                      name: e.label,
                    })

                    if (e.itemUnit.name === 'Rupiah') {
                      setDisableValueAmt(true)
                    } else {
                      setDisableValueAmt(false)
                    }
                  }}
                  initialOptions={[
                    {value: formik.values.evidenceId, label: formik.values.evidenceName},
                  ]}
                />
                {formik.touched.evidenceId && formik.errors.evidenceId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.evidenceId}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className='col-md-6'>
                <label htmlFor='satuan' className='form-label'>
                  Satuan
                </label>
                <ItemUnitsSelect
                  disabled={true}
                  form={formik}
                  field={formik.getFieldProps('itemUnitId')}
                />
              </div>
            </div>
            <div className='row mb-4'>
              <div className='col-6 mb-3'>
                <label htmlFor='qty' className='form-label required'>
                  Jumlah
                </label>
                <CurrencyInput
                  prefix=''
                  // groupSeparator='.'
                  // decimalSeparator=','
                  intlConfig={{locale: 'id-ID'}}
                  min={0}
                  value={formik.getFieldProps('qty').value?.toString()?.replace('.', ',')}
                  onValueChange={(event, name, values) => {
                    const parsedValue = event?.replace(',', '.') || '0'
                    formik.setFieldValue('qty', parsedValue)
                    if (disableValueAmt) {
                      formik.setFieldValue('valueAmt', parsedValue)
                    }
                  }}
                  name='qty'
                  className={clsx(
                    'form-control mb-3 mb-lg-0',
                    {'is-invalid': formik.touched.qty && formik.errors.qty},
                    {'is-valid': formik.touched.qty && !formik.errors.qty}
                  )}
                  autoComplete='off'
                  disabled={formik.isSubmitting || isLoading || disabled}
                  placeholder='Masukkan Jumlah'
                />

                {formik.touched.qty && formik.errors.qty && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.qty}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className='col-6 mb-3'>
                <label htmlFor='qty' className='form-label'>
                  Nilai Barang
                </label>
                <CurrencyInput
                  prefix=''
                  // groupSeparator='.'
                  // decimalSeparator=','
                  intlConfig={{locale: 'id-ID'}}
                  value={formik.getFieldProps('valueAmt').value}
                  onValueChange={(event, name, values) =>
                    formik.setFieldValue('valueAmt', event ?? '0')
                  }
                  name='valueAmt'
                  className={clsx(
                    'form-control mb-3 mb-lg-0',
                    {'is-invalid': formik.touched.valueAmt && formik.errors.valueAmt},
                    {'is-valid': formik.touched.valueAmt && !formik.errors.valueAmt}
                  )}
                  autoComplete='off'
                  disabled={formik.isSubmitting || isLoading || disabled || disableValueAmt}
                  placeholder='Masukkan Jumlah'
                />

                {formik.touched.valueAmt && formik.errors.valueAmt && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.valueAmt}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='row mb-4'>
              <div className='col-12 mb-3'>
                <label htmlFor='qty' className='form-label'>
                  Deskripsi
                </label>
                <textarea
                  className='form-control'
                  rows={3}
                  {...formik.getFieldProps('description')}
                  disabled={disabled}
                ></textarea>

                {formik.touched.description && formik.errors.description && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.description}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='d-flex justify-content-center'>
              {isEditEvidence && (
                <button
                  className='btn btn-sm btn btn-outline btn-outline-danger me-3'
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    formik.setValues({
                      description: '',
                      evidenceId: undefined,
                      evidenceName: '',
                      gender: undefined,
                      id: undefined,
                      itemUnitId: undefined,
                      itemUnitName: '',
                      key: '',
                      name: '',
                      qty: undefined,
                      valueAmt: undefined,
                    })
                    setEditEvidence(false)
                  }}
                >
                  Batal
                </button>
              )}
              {!disabled && (
                <button
                  type='button'
                  className='btn btn-sm btn-primary'
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    formik.handleSubmit()
                  }}
                >
                  {isEditEvidence ? 'Update Data' : 'Tambah Barang Bukti'}
                </button>
              )}
            </div>
            <hr />
            <label className='form-section-title mt-5'>
              <i className='fa-regular fa-file-lines'></i> List Barang Bukti
            </label>
            <div className='row'>
              <div className='col-md-12 mt-3'>
                <table
                  id='kt_table_student'
                  className='table table-striped table-rounded dataTable align-middle table-row-dashed no-footer gs-7 fs-6 gy-5'
                >
                  <thead>
                    <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                      <th style={{width: 50}}>No</th>
                      <th>Nama Barang</th>
                      <th>Jumlah</th>
                      <th>Satuan</th>
                      <th>Nilai Barang</th>
                      <th style={{width: 150}}>Action</th>
                    </tr>
                  </thead>
                  <tbody className='text-gray-600 fw-bold'>
                    {evidences.length > 0 ? (
                      evidences.map((row, i) => {
                        return (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{row.name ?? row.evidenceName ?? '-'}</td>
                            <td>{row.qty == null ? '-' : formatNumber(row.qty)}</td>
                            <td>{row.itemUnitName ?? '-'}</td>
                            <td>{row.valueAmt == null ? '-' : formatNumber(row.valueAmt)}</td>
                            <td>
                              {
                                <div>
                                  {/* begin::Menu item */}
                                  <button
                                    type={'button'}
                                    className='btn menu-link text-primary px-3'
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      const item = evidences[i]
                                      setInitialOptionEvidence([
                                        {value: item.evidenceId, label: item.evidenceName},
                                      ])
                                      formik.setValues({...item, index: i})
                                      setEditEvidence(true)
                                    }}
                                  >
                                    {disabled ? 'View' : 'Edit'}
                                  </button>
                                  {!disabled && (
                                    <>
                                      |
                                      <button
                                        type={'button'}
                                        className='btn menu-link text-primary px-3'
                                        data-kt-student-table-filter='delete_row'
                                        onClick={() => {
                                          setEvidence(evidences.filter((_, idx) => idx !== i))
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              }
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                            {isLoading ? '' : 'No matching records found'}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Formik>
    )
  }

  return useAccordion ? (
    <Accordion className='mb-3'>
      <Accordion.Item eventKey='0'>
        <Accordion.Header>Barang Bukti</Accordion.Header>
        <Accordion.Body>{FormComp()}</Accordion.Body>
      </Accordion.Item>
    </Accordion>
  ) : (
    FormComp()
  )
}

export default ReportEvidenceForm
